import { contrastRatio } from './contrast';

/**
 * Section colours (P5e, backlog §13.1): a very light pastel fill and the matching accent of its title pill and outline
 * once selected (white bold text on it: 4.5:1). Documents keep the plain fill hex; gray is the default of a new section.
 */
export const SECTION_COLORS = [
  { id: 'gray', fill: '#f4f4f2', accent: '#5c5c57' },
  { id: 'blue', fill: '#e6f0ff', accent: '#2a5fc4' },
  { id: 'green', fill: '#e5f5e2', accent: '#2c7139' },
  { id: 'yellow', fill: '#fff6d8', accent: '#80600a' },
  { id: 'orange', fill: '#ffeedf', accent: '#a9470f' },
  { id: 'red', fill: '#ffe7e4', accent: '#bb2a2f' },
  { id: 'pink', fill: '#ffe9f2', accent: '#ad2c68' },
  { id: 'purple', fill: '#f0eaff', accent: '#6740c9' },
] as const;
export type SectionColorId = (typeof SECTION_COLORS)[number]['id'];

/** Fills of earlier sections (the default before P5e, and frames): they read as gray. */
const LEGACY: Record<string, SectionColorId> = { '#f4f4f5': 'gray', '#ffffff': 'gray', '#fff': 'gray' };
const BY_FILL = new Map<string, SectionColorId>([...Object.entries(LEGACY), ...SECTION_COLORS.map((c) => [c.fill, c.id] as const)]);

export function sectionColorOf(fill: string): SectionColorId | null {
  return BY_FILL.get(fill.toLowerCase()) ?? null;
}

const WHITE = '#ffffff';

/** The pill and outline colour of a section: its pair's accent, or its own fill darkened until white text reads on it. */
export function sectionAccent(fill: string): string {
  const id = sectionColorOf(fill);
  if (id) return SECTION_COLORS.find((c) => c.id === id)!.accent;
  const hsl = toHsl(fill);
  if (!hsl) return SECTION_COLORS[0].accent;
  const [h, s] = hsl;
  for (let l = Math.min(hsl[2], 0.5); l > 0; l -= 0.02) {
    const hex = fromHsl(h, s, l);
    if (contrastRatio(hex, WHITE) >= 4.5) return hex;
  }
  return '#000000';
}

function toHsl(hex: string): [number, number, number] | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const full = m[1]!.length === 3 ? [...m[1]!].map((c) => c + c).join('') : m[1]!;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? ((g - b) / d + (g < b ? 6 : 0)) / 6 : max === g ? ((b - r) / d + 2) / 6 : ((r - g) / d + 4) / 6;
  return [h, s, l];
}

function fromHsl(h: number, s: number, l: number): string {
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number) => {
    const u = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
    const v = u < 1 / 6 ? p + (q - p) * 6 * u : u < 1 / 2 ? q : u < 2 / 3 ? p + (q - p) * (2 / 3 - u) * 6 : p;
    return Math.round(v * 255)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${channel(h + 1 / 3)}${channel(h)}${channel(h - 1 / 3)}`;
}
